import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminLeverageAPI, adminAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TrendingUp, Plus, Trash2, X, Users } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

export default function AdminLeverageFundsPage() {
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [form, setForm] = useState({ user_id: '', amount: '' });
  const [editRepay, setEditRepay] = useState(null);
  const [repayInput, setRepayInput] = useState('');

  const load = async () => {
    try {
      const res = await adminLeverageAPI.getAll();
      setFunds(res.data);
    } catch { toast.error('Failed to load leverage funds'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAssign = async () => {
    if (!form.user_id || !form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter user ID and amount'); return; }
    try {
      await adminLeverageAPI.assign({
        user_id: form.user_id, amount: parseFloat(form.amount),
      });
      toast.success('Leverage fund assigned');
      setShowAssign(false);
      setForm({ user_id: '', amount: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to assign'); }
  };

  const handleRemove = async (userId) => {
    try {
      await adminLeverageAPI.remove({ user_id: userId });
      toast.success('Leverage fund removed');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove'); }
  };

  const activeFunds = funds.filter(f => f.status === 'active');
  const inactiveFunds = funds.filter(f => f.status !== 'active');

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-leverage-funds">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h1 className="text-lg font-semibold font-['Outfit'] text-[var(--text-primary)]">Leverage Fund Management</h1>
              <p className="text-xs text-[var(--text-secondary)]">Assign and manage leverage funds for users</p>
            </div>
          </div>
          <button onClick={() => setShowAssign(true)} data-testid="assign-leverage-btn"
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm font-medium transition-all">
            <Plus className="w-4 h-4" /> Assign Leverage
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded">
            <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Active Leverage Funds</p>
            <p className="text-lg font-bold font-mono text-blue-400 mt-1">{activeFunds.length}</p>
          </div>
          <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded">
            <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Total Leverage Issued</p>
            <p className="text-lg font-bold font-mono text-[var(--text-primary)] mt-1">{formatCurrency(activeFunds.reduce((s, f) => s + f.amount, 0))}</p>
          </div>
          <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded">
            <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Avg Volume Completion</p>
            <p className="text-lg font-bold font-mono text-emerald-400 mt-1">
              {activeFunds.length > 0 ? (activeFunds.reduce((s, f) => s + (f.required_volume > 0 ? (f.completed_volume / f.required_volume) * 100 : 0), 0) / activeFunds.length).toFixed(1) : 0}%
            </p>
          </div>
        </div>

        {/* Active Funds Table */}
        <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded overflow-x-auto">
          <div className="px-4 py-3 border-b border-[var(--border-subtle)] flex items-center gap-2">
            <Users className="w-4 h-4 text-[var(--text-secondary)]" />
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">Active Leverage Funds</h2>
          </div>
          {activeFunds.length === 0 ? (
            <div className="p-8 text-center text-sm text-[var(--text-secondary)]">No active leverage funds</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-[var(--text-secondary)] uppercase border-b border-[var(--border-subtle)]">
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-right px-3 py-3">Leverage</th>
                  <th className="text-right px-3 py-3">Required Vol</th>
                  <th className="text-right px-3 py-3">Completed Vol</th>
                  <th className="text-right px-3 py-3">Repaid</th>
                  <th className="text-center px-3 py-3">Progress</th>
                  <th className="text-left px-3 py-3">Assigned</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {activeFunds.map((f) => {
                  const pct = f.required_volume > 0 ? Math.min((f.completed_volume / f.required_volume) * 100, 100) : 0;
                  return (
                    <tr key={f.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)]">
                      <td className="px-4 py-3">
                        <p className="text-xs font-medium text-[var(--text-primary)]">{f.user_name}</p>
                        <p className="text-[10px] text-[var(--text-tertiary)]">{f.user_email}</p>
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-blue-400 font-semibold">{formatCurrency(f.amount)}</td>
                      <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)]">{formatCurrency(f.required_volume)}</td>
                      <td className="px-3 py-3 text-right font-mono text-emerald-400">{formatCurrency(f.completed_volume)}</td>
                      <td className="px-3 py-3 text-right">
                        {editRepay === f.user_id ? (
                          <span className="flex items-center gap-1 justify-end">
                            <input type="number" step="any" value={repayInput} onChange={(e) => setRepayInput(e.target.value)}
                              onKeyDown={async (e) => { if (e.key === 'Enter') {
                                try { await adminAPI.updateLeverageRepayment(f.user_id, parseFloat(repayInput)); toast.success('Repayment updated'); setEditRepay(null); load(); } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); }
                              }}}
                              className="w-20 h-6 px-1.5 text-xs font-mono bg-[var(--bg-primary)] border border-emerald-500/30 rounded text-[var(--text-primary)] focus:outline-none" autoFocus />
                            <button onClick={async () => { try { await adminAPI.updateLeverageRepayment(f.user_id, parseFloat(repayInput)); toast.success('Repayment updated'); setEditRepay(null); load(); } catch(err) { toast.error(err.response?.data?.detail || 'Failed'); } }}
                              className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">Save</button>
                            <button onClick={() => setEditRepay(null)} className="text-[10px] text-[var(--text-tertiary)]">×</button>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 justify-end">
                            <span className={`font-mono text-xs ${(f.amount_repaid || 0) >= f.amount ? 'text-emerald-400' : 'text-amber-400'}`}>
                              {formatCurrency(f.amount_repaid || 0)} / {formatCurrency(f.amount)}
                            </span>
                            <button onClick={() => { setEditRepay(f.user_id); setRepayInput(String(f.amount_repaid || 0)); }}
                              data-testid={`edit-repay-${f.user_id}`}
                              className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--hover-bg)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">Edit</button>
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="w-full h-2 bg-[var(--bg-primary)] rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-[10px] text-center text-[var(--text-tertiary)] mt-0.5">{pct.toFixed(1)}%</p>
                      </td>
                      <td className="px-3 py-3 text-xs text-[var(--text-secondary)]">{f.assigned_at ? new Date(f.assigned_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => handleRemove(f.user_id)} data-testid={`remove-leverage-${f.user_id}`}
                          className="p-1.5 rounded bg-red-500/15 text-red-400 hover:bg-red-500/25" title="Remove">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Assign Modal */}
        {showAssign && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="assign-modal">
            <div className="w-full max-w-md bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-[var(--text-primary)]">Assign Leverage Fund</h2>
                <button onClick={() => setShowAssign(false)}><X className="w-5 h-5 text-[var(--text-secondary)]" /></button>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--text-secondary)]">User ID</Label>
                <Input type="text" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                  placeholder="Enter user ID" data-testid="assign-user-id"
                  className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--text-secondary)]">Leverage Amount (USD)</Label>
                <Input type="number" step="any" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="e.g. 2000" data-testid="assign-amount"
                  className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono h-10" />
              </div>
              <button onClick={handleAssign} data-testid="assign-submit-btn"
                className="w-full py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm font-medium">
                Assign Leverage Fund
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
